import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Layers,
  PlayCircle,
  FileSearch,
  PenTool,
} from "lucide-react";
import RevealText from "@/components/RevealText";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";
import SectorJourneySelector from "@/components/SectorJourneySelector";

const IMG_HERO =
  "https://images.unsplash.com/photo-1628270680011-41792b21de87?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

// Approved governed-change artwork — "The Governed Crossing".
const IMG_BLUEPRINT = "/media/governed-crossing.jpg";

// § 02 — the actors that must form dependable operational relationships.
const ACTORS_TOP = ["People", "Institutions", "Intelligent agents"];
const ACTORS_BOTTOM = ["Standards", "Networks", "Existing systems"];

// § 03 — governed engineering change, as a controlled state sequence.
const CHANGE_STATES = [
  { t: "Observed revision", tone: "ink" },
  { t: "Not previously governed", tone: "ink" },
  { t: "Frozen", tone: "frozen" },
  { t: "Responsible review", tone: "ink" },
  { t: "Re-checked", tone: "teal" },
  { t: "Released", tone: "teal" },
];

// § 03 — the Certified Operational Journey strip.
const COJ_STEPS = [
  { t: "Defined roles", tone: "ink" },
  { t: "Evidence boundaries", tone: "ink" },
  { t: "Authority and runtime policy", tone: "ink" },
  { t: "Control Decision Envelope", tone: "teal" },
  { t: "Responsible repair", tone: "ink" },
  { t: "Release", tone: "ink" },
  { t: "Receipts", tone: "gold" },
];

function scrollToProofs() {
  const el = document.getElementById("sector-journeys");
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div data-testid="home-page">
      {/* ── 1 · Reader / problem hero ──────────────────────── */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-36 md:pt-44"
        data-testid="hero"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-4"
            >
              <span className="h-px w-8 shrink-0 bg-teal" />
              <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-ink/50">
                Operational Engineering
              </span>
            </motion.div>

            <RevealText
              as="h1"
              testId="hero-heading"
              delay={0.15}
              lines={["Engineering trustworthy", "operational journeys"]}
              className="mt-8 font-serif text-[2.3rem] leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-[3.5rem]"
            />

            <Reveal delay={0.7} className="mt-8 max-w-xl">
              <p className="font-sans text-lg leading-relaxed text-ink/70 md:text-xl">
                KATLAS helps organisations move from research, policy and technical
                possibility to dependable real-world capability—across people,
                intelligent agents, standards and existing systems.
              </p>
            </Reveal>

            <Reveal delay={0.78} className="mt-5 max-w-xl">
              <p className="font-sans text-base leading-relaxed text-ink/60 md:text-lg">
                We design the roles, authority, evidence conditions and learning
                mechanisms through which consequential action can proceed with
                confidence.
              </p>
            </Reveal>

            <Reveal delay={0.85} className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={scrollToProofs}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-stone"
              >
                See a governed journey move
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.75} />
              </button>
              <Link
                to="/cierge-studio"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-2 border border-ink/25 px-6 py-3.5 font-sans text-sm text-ink transition-colors duration-300 hover:border-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-teal"
              >
                Bring us a journey that must become dependable
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
              </Link>
            </Reveal>

            {/* CAR line */}
            <Reveal delay={0.95} className="mt-12 border-l-2 border-teal pl-5">
              <p className="font-serif text-lg italic leading-relaxed text-ink md:text-xl" data-testid="car-line">
                Custody controls the evidence.
                <br />
                Authority governs the action.
                <br />
                Receipts prove what happened.
              </p>
            </Reveal>
          </div>

          {/* Clipped, spotlighted image with parallax + receipt tag */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="clip-frame relative aspect-[3/4] overflow-hidden border border-ink/15 bg-ink">
                <motion.img
                  src={IMG_HERO}
                  alt="Minimal concrete architecture — structure, rhythm and a defined boundary."
                  style={{ y: imgY, scale: imgScale }}
                  className="h-full w-full object-cover object-center opacity-95"
                  loading="eager"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="absolute -bottom-5 -left-4 border border-gold/45 bg-ink px-5 py-3 md:-left-8"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                  receipt · verified
                </p>
                <p className="mt-1 font-mono text-xs text-stone/80">0x9f3a…c721</p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="mt-20 md:mt-28" />
      </section>

      {/* ── 2 · The operational challenge ───────────────────── */}
      <section
        id="operational-challenge"
        className="border-t border-ink/12 py-24 md:py-32"
        data-testid="operational-challenge"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-8 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ 02</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              The operational challenge
            </span>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <RevealText
                as="h2"
                animateOnView
                lines={["Intelligence alone", "does not create", "dependable operations."]}
                className="font-serif text-2xl leading-[1.12] tracking-tight text-ink sm:text-3xl lg:text-[2.5rem]"
              />

              <Reveal delay={0.15} className="mt-8">
                <p className="max-w-xl font-sans text-base leading-relaxed text-ink/70 md:text-lg">
                  People, organisations, intelligent agents, standards and existing
                  systems increasingly have to act together. What is missing is not
                  more intelligence—it is the ability to form dependable, accountable
                  operational relationships.
                </p>
              </Reveal>

              <Reveal delay={0.25} className="mt-6">
                <p className="max-w-xl font-sans text-base leading-relaxed text-ink/60">
                  The future depends on the quality of those relationships—and on the
                  operational journey through which they proceed.
                </p>
              </Reveal>
            </div>

            {/* Restrained relationship diagram — the journey holds the centre */}
            <div className="lg:col-span-7">
              <Reveal delay={0.2}>
                <div
                  className="border border-ink/15 bg-stone p-8 md:p-12"
                  data-testid="relationship-diagram"
                >
                  {/* actors — upper */}
                  <div className="grid grid-cols-3 gap-3 md:gap-5">
                    {ACTORS_TOP.map((a) => (
                      <div key={a} className="flex flex-col items-center text-center">
                        <div className="flex min-h-[3.25rem] w-full items-center justify-center border border-ink/20 bg-white/60 px-2 py-2.5 font-mono text-[10px] uppercase leading-tight tracking-[0.12em] text-ink/75 md:text-[11px]">
                          {a}
                        </div>
                        <span className="mt-3 hidden h-6 w-px bg-ink/20 md:block" />
                      </div>
                    ))}
                  </div>

                  {/* the journey — centre */}
                  <div className="my-4 border-2 border-teal bg-white px-6 py-6 text-center md:my-2">
                    <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-teal">
                      Holds the centre
                    </p>
                    <p className="mt-2 font-serif text-xl text-ink md:text-2xl">
                      The operational journey
                    </p>
                    <p className="mt-2 font-sans text-xs leading-relaxed text-ink/55">
                      Designed, bounded, tested, repaired and governed
                    </p>
                  </div>

                  {/* actors — lower */}
                  <div className="grid grid-cols-3 gap-3 md:gap-5">
                    {ACTORS_BOTTOM.map((a) => (
                      <div key={a} className="flex flex-col items-center text-center">
                        <span className="mb-3 hidden h-6 w-px bg-ink/20 md:block" />
                        <div className="flex min-h-[3.25rem] w-full items-center justify-center border border-ink/20 bg-white/60 px-2 py-2.5 font-mono text-[10px] uppercase leading-tight tracking-[0.12em] text-ink/75 md:text-[11px]">
                          {a}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3 · Governed change analogy ────────────────────── */}
      <section
        id="governed-change"
        className="border-t border-ink/12 py-24 md:py-32"
        data-testid="governed-change"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-8 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ 03</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              Governed change
            </span>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
            {/* Approved governed-change artwork */}
            <div className="lg:col-span-5">
              <div className="clip-frame relative aspect-[2/3] overflow-hidden border border-ink/15 bg-stone">
                <img
                  src={IMG_BLUEPRINT}
                  alt="The Governed Crossing — a bridge crossing engineered as a governed revision: request, validate, decide, repair, release, receipt."
                  className="h-full w-full object-cover object-center"
                  loading="lazy"
                  data-testid="governed-change-artwork"
                />
              </div>
            </div>

            {/* Statement + controlled state sequence */}
            <div className="lg:col-span-7">
              <RevealText
                as="h2"
                animateOnView
                lines={["Great engineering has", "always required governed", "change before construction", "proceeds."]}
                className="font-serif text-2xl leading-[1.12] tracking-tight text-ink sm:text-3xl lg:text-[2.5rem]"
              />

              <Reveal delay={0.15} className="mt-8">
                <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/70 md:text-lg">
                  Certified Operational Journeys apply the same discipline to decisions
                  spanning organisations, software, AI, IoT and regulated operations.
                </p>
              </Reveal>

              <RevealGroup className="mt-10 flex flex-wrap items-center gap-x-2 gap-y-3" testId="governed-change-sequence">
                {CHANGE_STATES.map((s, i, arr) => (
                  <RevealItem key={s.t} className="flex items-center gap-2">
                    <span
                      className={`px-3.5 py-2 font-mono text-xs uppercase tracking-[0.14em] ${
                        s.tone === "frozen"
                          ? "border border-ink bg-ink text-stone"
                          : s.tone === "teal"
                          ? "border border-teal text-teal"
                          : "border border-ink/25 text-ink/75"
                      }`}
                    >
                      {s.t}
                    </span>
                    {i < arr.length - 1 && (
                      <ArrowRight className="h-4 w-4 shrink-0 text-ink/30" strokeWidth={1.5} />
                    )}
                  </RevealItem>
                ))}
              </RevealGroup>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4 · Certified Operational Journey strip ────────── */}
      <section
        className="border-t border-ink/12 py-24 md:py-32"
        data-testid="certified-operational-journey"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-8 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ 04</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              The Certified Operational Journey
            </span>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <RevealText
                as="h2"
                animateOnView
                lines={["From source conditions", "to governed outcome."]}
                className="font-serif text-3xl leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl"
              />
              <Reveal delay={0.2} className="mt-8 border-l-2 border-teal pl-6">
                <p className="font-serif text-lg italic leading-snug text-ink md:text-xl">
                  A Certified Operational Journey makes the control decision explicit,
                  testable and provable across organisational boundaries.
                </p>
              </Reveal>
              <Reveal delay={0.3} className="mt-10">
                <Link
                  to="/engineering-architecture"
                  data-testid="coj-platform-cta"
                  className="group inline-flex items-center gap-2 border border-ink/25 px-6 py-3.5 font-sans text-sm text-ink transition-colors duration-300 hover:border-ink"
                >
                  Understand the engineering challenge
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.75} />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <div className="border border-ink/15" data-testid="coj-strip">
                {COJ_STEPS.map((s, i, arr) => (
                  <Reveal key={s.t} delay={i * 0.06}>
                    <div
                      className={`flex items-center gap-5 border-b border-ink/12 p-5 last:border-b-0 md:p-6 ${
                        s.tone === "gold"
                          ? "border-l-2 border-l-gold"
                          : s.tone === "teal"
                          ? "border-l-2 border-l-teal"
                          : ""
                      }`}
                    >
                      <span className="font-mono text-xs text-teal">{`0${i + 1}`}</span>
                      <h3 className="font-serif text-lg text-ink md:text-2xl">{s.t}</h3>
                      {i < arr.length - 1 && (
                        <ArrowRight className="ml-auto h-4 w-4 rotate-90 text-ink/25" strokeWidth={1.5} />
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5 · One architecture, four operational proofs ─── */}
      <SectorJourneySelector index="§ 05" />

      {/* ── 6 · Evidence and engagement ────────────────────── */}
      <section className="py-24 md:py-32" data-testid="evidence-engagement">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-8 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ 06</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              Where next
            </span>
          </div>

          <RevealGroup className="grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 md:grid-cols-2">
            {/* Explore the engineering architecture */}
            <RevealItem className="bg-stone">
              <Link
                to="/engineering-architecture"
                data-testid="engage-platform"
                className="group flex h-full flex-col p-8 transition-colors duration-300 hover:bg-ink/[0.03] md:p-10"
              >
                <Layers className="h-5 w-5 text-teal" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl text-ink">Explore the engineering architecture</h3>
                <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-ink/70">
                  The engineering discipline — role nodes, protocol, the Control Decision
                  Envelope, shared proof and receipts.
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 font-sans text-sm text-ink/75 transition-colors group-hover:text-teal">
                  Engineering architecture
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.75} />
                </span>
              </Link>
            </RevealItem>

            {/* Watch the demonstrations */}
            <RevealItem className="bg-stone">
              <button
                type="button"
                onClick={scrollToProofs}
                data-testid="engage-demonstrations"
                className="group flex h-full w-full flex-col p-8 text-left transition-colors duration-300 hover:bg-ink/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal md:p-10"
              >
                <PlayCircle className="h-5 w-5 text-teal" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl text-ink">Watch the demonstrations</h3>
                <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-ink/70">
                  Finance, Health, Logistics and Passenger — one reusable architecture
                  shown in four operational contexts.
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 font-sans text-sm text-ink/75 transition-colors group-hover:text-teal">
                  Operational proofs
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.75} />
                </span>
              </button>
            </RevealItem>

            {/* Review engineering evidence */}
            <RevealItem className="bg-stone">
              <Link
                to="/evidence"
                data-testid="engage-evidence"
                className="group flex h-full flex-col p-8 transition-colors duration-300 hover:bg-ink/[0.03] md:p-10"
              >
                <FileSearch className="h-5 w-5 text-teal" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl text-ink">Review engineering evidence</h3>
                <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-ink/70">
                  White papers, technical notes, standards, architecture and downloadable
                  artefacts for technical diligence.
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 font-sans text-sm text-ink/75 transition-colors group-hover:text-teal">
                  Evidence
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.75} />
                </span>
              </Link>
            </RevealItem>

            {/* Design a Certified Operational Journey */}
            <RevealItem className="bg-ink text-stone">
              <Link
                to="/cierge-studio"
                data-testid="engage-studio"
                className="group flex h-full flex-col p-8 transition-colors duration-300 hover:bg-ink/90 md:p-10"
              >
                <PenTool className="h-5 w-5 text-teal" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl text-stone">
                  Design a Certified Operational Journey
                </h3>
                <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-stone/70">
                  Bring one consequential handoff. The Cierge Design Studio maps roles,
                  authority, evidence, exceptions, repair and release.
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 font-sans text-sm text-stone/85 transition-colors group-hover:text-teal">
                  Cierge Design Studio
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
                </span>
              </Link>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>
    </div>
  );
}
